import * as React from 'react';
import { useSelector } from 'react-redux';

import { removeCookie } from '../../../../browser/utils/js-cookie';
import { MOSCOW_AND_REGION } from '../../../../packages/Filters/shared/constants/regions';
import { getTermValue } from '../../../../packages/JsonQuery';
import { getIsDealSaleBannerRotationEnabled } from '../../../selectors/dealSaleBanner';
import { getDealType } from '../../../selectors/getDealType';
import { getJsonQuery } from '../../../selectors/getJsonQuery';
import { getNewbuildingSimilarAdPath } from '../../../selectors/getNewbuildingSimilarAdPath';
import { getNewbuildingSimilarOffers } from '../../../selectors/getNewbuildingSimilarOffers';
import { getOfferType } from '../../../selectors/getOfferType';
import { selectIsNewbuildingConsultBannerAvailable } from '../../../selectors/newbuildingConsultBanner';
import { getIsNewbuildingBannerRotationEnabled } from '../../../selectors/newbuildingEntryBanner';
import { getIsRosbankPilotBannerRotationEnabled } from '../../../selectors/rosbankPilotBanner';
import { selectSuburbanBlackFridayBannerUrl } from '../../../selectors/selectSuburbanBlackFridayBannerUrl';
import { EDealType } from '../../../types/dealType';
import { useApplicationContext } from '../../../utils/applicationContext';
import { FOfferType, isSuburban } from '../../../utils/category';
import { getNewbuildingEntryBannerInfo } from '../../../utils/getNewbuildingEntryBannerInfo';
import { getNewbuildingMixedEntryBannerType } from '../../../utils/getNewbuildingMixedEntryBannerType';
import { CountrysideBuildersBannerContainer } from '../../components/CountrysideBuildersBannerContainer';
import { NewbuildingConsultBannerConatiner } from '../../components/NewbuildingConsultBanner';
import {
  NewbuildingEntryResaleBlock,
  trackNewbuildingEntryResaleBlockShow,
  trackNewbuildingEntryResaleBlockButtonClick,
  trackNewbuildingEntryResaleBlockItemClick,
} from '../../components/NewbuildingEntryResaleBlock';
import { SuburbanBlackFridayBanner } from '../../components/SuburbanBlackFridayBanner';
import { IIntegrateAdOptions } from '../../components/offers/helpers';
import { ObservableBannerServicePlace } from '../../components/service_places/banner/container';
import { DealSaleBannerContainer } from '../DealSaleBannerContainer';
import { NewbuildingEntryBannerContainer } from '../NewbuildingEntryBannerContainer';
import { RosbankPilotBannerContainer } from '../RosbankPilotBannerContainer';

export interface IBannerConatinerProps {
  options: IIntegrateAdOptions;
  position: number;
  isAgent?: boolean;
  locationId?: number;
  isCountrysideBuildersBannerAvailable?: boolean;
}

export function BannerConatiner({
  options,
  position,
  isAgent,
  locationId,
  isCountrysideBuildersBannerAvailable,
}: IBannerConatinerProps) {
  const isNewbuildingBannerRotationEnabled = useSelector(getIsNewbuildingBannerRotationEnabled);
  const isDealSaleBannerRandomRotationEnabled = useSelector(getIsDealSaleBannerRotationEnabled);
  const isRosbankPilotBannerRandomRotationEnabled = useSelector(getIsRosbankPilotBannerRotationEnabled);
  const isNbEntryConsultBannerAvailable = useSelector(selectIsNewbuildingConsultBannerAvailable);
  const dealType = useSelector(getDealType);
  const offerType = useSelector(getOfferType);
  const jsonQuery = useSelector(getJsonQuery);
  const nbSimilarOffers = useSelector(getNewbuildingSimilarOffers);
  const nbSimilarAdPath = useSelector(getNewbuildingSimilarAdPath);
  const { logger, config } = useApplicationContext();
  const villageBuildersBannerShow = config.get<boolean>('showCountryBuildersBanner');
  const isMortgageBannerEnabled = config.get<boolean>('mortgage.isSerpBannerEnabled');
  const isDealSaleBannerEnabled = config.get<boolean>('frontend-serp.DealSaleBanner.Enabled');
  const isFromDeveloperFilterEnabled = getTermValue('from_developer')(jsonQuery);
  const suburbanBlackFridayUrl = useSelector(selectSuburbanBlackFridayBannerUrl);

  React.useEffect(
    /* istanbul ignore next */ () => {
      if (!isCountrysideBuildersBannerAvailable) {
        removeCookie('buildersBannerShown');
      }
    },
    [isCountrysideBuildersBannerAvailable],
  );

  // Определяем, нужно ли показывать баннер консультации по новостройкам
  if (isNbEntryConsultBannerAvailable && !isAgent) {
    return <NewbuildingConsultBannerConatiner />;
  }

  // Определяем, нужно ли показывать баннер ведущий на выдачу новостроек в смешенной выдаче покупки квартир
  if (isNewbuildingBannerRotationEnabled && offerType === FOfferType.Flat && dealType === EDealType.Sale) {
    const bannerType = getNewbuildingMixedEntryBannerType(config);

    if (bannerType) {
      return <NewbuildingEntryBannerContainer type={bannerType} />;
    }
  }

  // Определяем, нужно ли показывать блоки объявлений из новостроек в выдаче квартир вторички
  if (
    isNewbuildingBannerRotationEnabled &&
    nbSimilarOffers &&
    nbSimilarAdPath &&
    offerType === FOfferType.FlatOld &&
    dealType === EDealType.Sale
  ) {
    const { title, description } = getNewbuildingEntryBannerInfo('resale', config, logger);

    return (
      <NewbuildingEntryResaleBlock
        onShow={trackNewbuildingEntryResaleBlockShow(jsonQuery)}
        onButtonClick={trackNewbuildingEntryResaleBlockButtonClick(jsonQuery)}
        onItemClick={trackNewbuildingEntryResaleBlockItemClick(jsonQuery)}
        url={nbSimilarAdPath}
        title={title}
        description={description}
        items={nbSimilarOffers}
      />
    );
  }

  // Если в загородке в продаже акция "Черная пятница" активна, то показываем баннер
  if (isSuburban(offerType) && dealType === EDealType.Sale && suburbanBlackFridayUrl) {
    return <SuburbanBlackFridayBanner link={suburbanBlackFridayUrl} />;
  }

  if (
    (offerType & FOfferType.Land) !== 0 &&
    dealType === EDealType.Sale &&
    villageBuildersBannerShow &&
    locationId &&
    MOSCOW_AND_REGION.includes(locationId) &&
    isCountrysideBuildersBannerAvailable
  ) {
    return <CountrysideBuildersBannerContainer />;
  }

  // Если баннер пилота с Росбанком включен, показываем его
  if (isRosbankPilotBannerRandomRotationEnabled) {
    return <RosbankPilotBannerContainer serpType={dealType} offerType={offerType} />;
  }

  // Если баннер "Циан.Сделка" включен, показываем его
  if (isDealSaleBannerRandomRotationEnabled && isDealSaleBannerEnabled && !isFromDeveloperFilterEnabled) {
    return <DealSaleBannerContainer />;
  }

  // Если ипотека выключена, то ничего не отображаем
  if (!isMortgageBannerEnabled) {
    return null;
  }

  // В остальных случаях показываем банер ипотеки
  return <ObservableBannerServicePlace options={options} position={position} />;
}
